import {DcColorType} from '@shared-types';
import {Cached} from '@utils';
import {CancelCycleStatus, CompleteCycleStatus, CycleStatusType, DenyCycleStatus, FormCycleType, TemplateCycleType} from './cycle-status.type';

export type ICycleStatusData = {
	label: string; // translation key: pages.cycles.status.[CycleStatusType]
	icon: string;
	colors: {
		color: `var(${DcColorType})`;
		bg: `var(${DcColorType})`;
	};
	relatedStatuses?: CycleStatusType[];
	isProcessing?: boolean; // Statuses ending with "ing" are processing states
};

export type ICycleStatusCategory = {
	label: string;
	icon: string;
	tooltip: string;
	statuses: Partial<Record<CycleStatusType, ICycleStatusData>>;
};

export enum CycleStatusCategoryType {
	ACTIVE = 'ACTIVE',
	COMPLETED = 'COMPLETED',
	CANCELED = 'CANCELED',
	TEMPLATES = 'TEMPLATES',
	FORMS = 'FORMS',
}

export class CycleStatusData {
	static getCycleStatusLabel(status: CycleStatusType): string {
		// Handle special cases
		if (status === CycleStatusType.PrivateTemplate || status === CycleStatusType.PublicTemplate) {
			return 'pages.cycles.types.template';
		}

		if (status === CycleStatusType.PrivateForm || status === CycleStatusType.PublicForm || status === CycleStatusType.FormFill) {
			return 'pages.cycles.types.form';
		}

		// Handle regular cases
		return 'pages.cycles.status.' + status.toLowerCase();
	}

	static getCycleStatusTooltip(status: CycleStatusType): string {
		// Handle special cases
		if (status === CycleStatusType.PrivateTemplate || status === CycleStatusType.PublicTemplate) {
			return 'pages.cycles.tooltips.template';
		}

		if (status === CycleStatusType.PrivateForm || status === CycleStatusType.PublicForm || status === CycleStatusType.FormFill) {
			return 'pages.cycles.tooltips.form';
		}

		// Handle regular cases
		return 'pages.cycles.tooltips.' + status.toLowerCase();
	}

	static getCycleStatusIcon(status: CycleStatusType): string {
		switch (status) {
			case CycleStatusType.Cycling:
				return 'fa-duotone fa-rotate';

			case CycleStatusType.Completing:
			case CycleStatusType.Completed:
				return 'fa-duotone fa-check-circle';

			case CycleStatusType.Denying:
			case CycleStatusType.Denied:
				return 'fa-duotone fa-times-circle';

			case CycleStatusType.Canceling:
			case CycleStatusType.Canceled:
				return 'fa-duotone fa-ban';

			case CycleStatusType.Expired:
				return 'fa-duotone fa-hourglass-end';

			case CycleStatusType.Draft:
				return 'fa-duotone fa-file-lines';

			case CycleStatusType.PrivateTemplate:
			case CycleStatusType.PublicTemplate:
				return 'fa-duotone fa-copy';

			case CycleStatusType.PrivateForm:
			case CycleStatusType.PublicForm:
			case CycleStatusType.FormFill:
				return 'fa-duotone fa-clipboard-list';

			default:
				return 'fa-duotone fa-file';
		}
	}

	@Cached()
	static getCycleStatusColor(status: CycleStatusType): ICycleStatusData['colors'] {
		let colorType: DcColorType = DcColorType.Primary;
		
		switch (status) {
			case CycleStatusType.Cycling:
				colorType = DcColorType.Primary;
				break;

			case CycleStatusType.Expired:
				colorType = DcColorType.Accent;
				break;

			case CycleStatusType.Draft:
			case CycleStatusType.PrivateTemplate:
			case CycleStatusType.PublicTemplate:
			case CycleStatusType.PrivateForm:
			case CycleStatusType.PublicForm:
				colorType = DcColorType.Secondary;
				break;

			case CycleStatusType.Completing:
			case CycleStatusType.Completed:
				colorType = DcColorType.Success;
				break;

			case CycleStatusType.Denying:
			case CycleStatusType.Denied:
				colorType = DcColorType.Warn;
				break;

			case CycleStatusType.Canceling:
			case CycleStatusType.Canceled:
				colorType = DcColorType.Error;
				break;

			case CycleStatusType.FormFill:
				colorType = DcColorType.Primary;
				break;

			default:
				colorType = DcColorType.Primary;
				break;
		}
		
		return {
			color: `var(${colorType})`,
			bg: `var(${colorType}-50)`,
		};
	}

	@Cached()
	static getCycleStatusCategoriesData(): Record<CycleStatusCategoryType, ICycleStatusCategory> {
		return {
			[CycleStatusCategoryType.ACTIVE]: {
				label: 'pages.cycles.status.category.active.label',
				icon: 'fa-duotone fa-rotate',
				tooltip: 'pages.cycles.status.category.active.tooltip',
				statuses: {
					[CycleStatusType.Cycling]: {
						label: CycleStatusData.getCycleStatusLabel(CycleStatusType.Cycling),
						icon: CycleStatusData.getCycleStatusIcon(CycleStatusType.Cycling),
						colors: CycleStatusData.getCycleStatusColor(CycleStatusType.Cycling),
						isProcessing: false,
					},
					[CycleStatusType.Draft]: {
						label: CycleStatusData.getCycleStatusLabel(CycleStatusType.Draft),
						icon: CycleStatusData.getCycleStatusIcon(CycleStatusType.Draft),
						colors: CycleStatusData.getCycleStatusColor(CycleStatusType.Draft),
						isProcessing: false,
					},
					[CycleStatusType.FormFill]: {
						label: CycleStatusData.getCycleStatusLabel(CycleStatusType.FormFill),
						icon: CycleStatusData.getCycleStatusIcon(CycleStatusType.FormFill),
						colors: CycleStatusData.getCycleStatusColor(CycleStatusType.FormFill),
						isProcessing: false,
					},
				},
			},
			[CycleStatusCategoryType.COMPLETED]: {
				label: 'pages.cycles.status.category.completed.label',
				icon: 'fa-duotone fa-check-circle',
				tooltip: 'pages.cycles.status.category.completed.tooltip',
				statuses: {
					[CycleStatusType.Completing]: {
						label: CycleStatusData.getCycleStatusLabel(CycleStatusType.Completing),
						icon: CycleStatusData.getCycleStatusIcon(CycleStatusType.Completing),
						colors: CycleStatusData.getCycleStatusColor(CycleStatusType.Completing),
						relatedStatuses: [CycleStatusType.Completed],
						isProcessing: true,
					},
					[CycleStatusType.Completed]: {
						label: CycleStatusData.getCycleStatusLabel(CycleStatusType.Completed),
						icon: CycleStatusData.getCycleStatusIcon(CycleStatusType.Completed),
						colors: CycleStatusData.getCycleStatusColor(CycleStatusType.Completed),
						relatedStatuses: [CycleStatusType.Completing],
						isProcessing: false,
					},
					[CycleStatusType.Denying]: {
						label: CycleStatusData.getCycleStatusLabel(CycleStatusType.Denying),
						icon: CycleStatusData.getCycleStatusIcon(CycleStatusType.Denying),
						colors: CycleStatusData.getCycleStatusColor(CycleStatusType.Denying),
						relatedStatuses: [CycleStatusType.Denied],
						isProcessing: true,
					},
					[CycleStatusType.Denied]: {
						label: CycleStatusData.getCycleStatusLabel(CycleStatusType.Denied),
						icon: CycleStatusData.getCycleStatusIcon(CycleStatusType.Denied),
						colors: CycleStatusData.getCycleStatusColor(CycleStatusType.Denied),
						relatedStatuses: [CycleStatusType.Denying],
						isProcessing: false,
					},
				},
			},
			[CycleStatusCategoryType.CANCELED]: {
				label: 'pages.cycles.status.category.canceled.label',
				icon: 'fa-duotone fa-ban',
				tooltip: 'pages.cycles.status.category.canceled.tooltip',
				statuses: {
					[CycleStatusType.Canceling]: {
						label: CycleStatusData.getCycleStatusLabel(CycleStatusType.Canceling),
						icon: CycleStatusData.getCycleStatusIcon(CycleStatusType.Canceling),
						colors: CycleStatusData.getCycleStatusColor(CycleStatusType.Canceling),
						relatedStatuses: [CycleStatusType.Canceled],
						isProcessing: true,
					},
					[CycleStatusType.Canceled]: {
						label: CycleStatusData.getCycleStatusLabel(CycleStatusType.Canceled),
						icon: CycleStatusData.getCycleStatusIcon(CycleStatusType.Canceled),
						colors: CycleStatusData.getCycleStatusColor(CycleStatusType.Canceled),
						relatedStatuses: [CycleStatusType.Canceling],
						isProcessing: false,
					},
					[CycleStatusType.Expired]: {
						label: CycleStatusData.getCycleStatusLabel(CycleStatusType.Expired),
						icon: CycleStatusData.getCycleStatusIcon(CycleStatusType.Expired),
						colors: CycleStatusData.getCycleStatusColor(CycleStatusType.Expired),
						isProcessing: false,
					},
				},
			},
			[CycleStatusCategoryType.TEMPLATES]: {
				label: 'pages.cycles.status.category.templates.label',
				icon: 'fa-duotone fa-copy',
				tooltip: 'pages.cycles.status.category.templates.tooltip',
				statuses: {
					[CycleStatusType.PrivateTemplate]: {
						label: CycleStatusData.getCycleStatusLabel(CycleStatusType.PrivateTemplate),
						icon: CycleStatusData.getCycleStatusIcon(CycleStatusType.PrivateTemplate),
						colors: CycleStatusData.getCycleStatusColor(CycleStatusType.PrivateTemplate),
						isProcessing: false,
					},
					[CycleStatusType.PublicTemplate]: {
						label: CycleStatusData.getCycleStatusLabel(CycleStatusType.PublicTemplate),
						icon: CycleStatusData.getCycleStatusIcon(CycleStatusType.PublicTemplate),
						colors: CycleStatusData.getCycleStatusColor(CycleStatusType.PublicTemplate),
						isProcessing: false,
					},
				},
			},
			[CycleStatusCategoryType.FORMS]: {
				label: 'pages.cycles.status.category.forms.label',
				icon: 'fa-duotone fa-clipboard-list',
				tooltip: 'pages.cycles.status.category.forms.tooltip',
				statuses: {
					[CycleStatusType.PrivateForm]: {
						label: CycleStatusData.getCycleStatusLabel(CycleStatusType.PrivateForm),
						icon: CycleStatusData.getCycleStatusIcon(CycleStatusType.PrivateForm),
						colors: CycleStatusData.getCycleStatusColor(CycleStatusType.PrivateForm),
						isProcessing: false,
					},
					[CycleStatusType.PublicForm]: {
						label: CycleStatusData.getCycleStatusLabel(CycleStatusType.PublicForm),
						icon: CycleStatusData.getCycleStatusIcon(CycleStatusType.PublicForm),
						colors: CycleStatusData.getCycleStatusColor(CycleStatusType.PublicForm),
						isProcessing: false,
					},
				},
			},
		};
	}

	// Utility method to get status data for a specific cycle status
	static getStatusData(status: CycleStatusType): ICycleStatusData {
		const allCategories = CycleStatusData.getCycleStatusCategoriesData();

		for (const categoryKey in allCategories) {
			const category = allCategories[categoryKey as CycleStatusCategoryType];

			if (status in category.statuses) {
				return category.statuses[status];
			}
		}

		// Default status data if not found
		return {
			label: CycleStatusData.getCycleStatusLabel(status),
			icon: 'fa-duotone fa-file',
			colors: {
				color: `var(${DcColorType.Primary})`,
				bg: `var(${DcColorType.Primary})`,
			},
		};
	}

	// Utility method to get related statuses
	static getRelatedStatus(status: CycleStatusType): CycleStatusType | undefined {
		const statusData = CycleStatusData.getStatusData(status);

		if (statusData.relatedStatuses && statusData.relatedStatuses.length > 0) {
			return statusData.relatedStatuses[0];
		}

		return undefined;
	}

	// Utility method to determine if a status is a processing status (ending with "ing")
	static isProcessingStatus(status: CycleStatusType): boolean {
		const statusData = CycleStatusData.getStatusData(status);
		return statusData.isProcessing === true;
	}

	// Utility method to group related statuses
	static getGroupedStatuses(): Map<string, CycleStatusType[]> {
		const result = new Map<string, CycleStatusType[]>();

		// Group cancel statuses
		result.set('cancel', Object.values(CancelCycleStatus));

		// Group complete statuses
		result.set('complete', Object.values(CompleteCycleStatus));

		// Group deny statuses
		result.set('deny', Object.values(DenyCycleStatus));

		// Group template statuses
		result.set('template', Object.values(TemplateCycleType));

		// Group form statuses
		result.set('form', Object.values(FormCycleType));

		return result;
	}
}
